import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import AirlinesAdminDashboard from './pages/AirlinesAdminDashboard';
import AirlinesManagerDashboard from './pages/AirlinesManagerDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user?.role) {
      case 'super_admin': return <Navigate to="/super-admin/dashboard" replace />;
      case 'manager': return <Navigate to="/manager/dashboard" replace />;
      case 'airlines_admin': return <Navigate to="/airlines-admin/dashboard" replace />;
      case 'airlines_manager': return <Navigate to="/airlines-manager/dashboard" replace />;
      default: return <Navigate to="/login" replace />;
    }
  }
  
  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'super_admin': return <Navigate to="/super-admin/dashboard" replace />;
    case 'manager': return <Navigate to="/manager/dashboard" replace />;
    case 'airlines_admin': return <Navigate to="/airlines-admin/dashboard" replace />;
    case 'airlines_manager': return <Navigate to="/airlines-manager/dashboard" replace />;
    default: return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<DashboardRouter />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin/dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/airlines" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/managers" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/assign-me" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/visa-applications" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/error-pool" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/finalize-pool" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/reassign" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/reports" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/currency-minimum-balances" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Manager Routes (My Side) */}
          <Route path="/manager/dashboard" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/assign-me" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/visa-applications" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/error-pool" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/finalize-pool" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/reports" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          
          {/* Airlines Admin Routes */}
          <Route path="/airlines-admin/dashboard" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/visa-applications" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/error-pool" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/finalize-pool" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/managers" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/staff" element={<Navigate to="/airlines-admin/dashboard" replace />} />
          <Route path="/airlines-admin/profile" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/billing" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/reports" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-admin/api-integration" element={
            <ProtectedRoute allowedRoles={['airlines_admin']}>
              <AirlinesAdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Airlines Manager Routes */}
          <Route path="/airlines-manager/dashboard" element={
            <ProtectedRoute allowedRoles={['airlines_manager']}>
              <AirlinesManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-manager/visa-applications" element={
            <ProtectedRoute allowedRoles={['airlines_manager']}>
              <AirlinesManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-manager/error-pool" element={
            <ProtectedRoute allowedRoles={['airlines_manager']}>
              <AirlinesManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-manager/finalize-pool" element={
            <ProtectedRoute allowedRoles={['airlines_manager']}>
              <AirlinesManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/airlines-manager/reports" element={
            <ProtectedRoute allowedRoles={['airlines_manager']}>
              <AirlinesManagerDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

